/**
 * SPIKE (claude/opencode-framework-eval): opencode.json config builder.
 *
 * Maps a framework-agnostic ProvisionInput onto opencode's native single-file
 * config (`opencode.json`). This is the opencode analogue of the claude-code
 * adapter's `buildSettingsJson` + `buildMcpJson`, collapsed into one file the
 * way opencode expects.
 *
 * Key format differences from Claude Code that this file encodes:
 *   - MCP servers live under `mcp` (not `mcpServers`); a local server is
 *     `{ type: 'local', command: [bin, ...args], environment: {...} }` — note
 *     `command` is a single array and env is `environment`, not `env`.
 *   - Runtime env substitution uses `{env:NAME}` (not Claude Code's `${NAME}`).
 *   - Tool enforcement is a `permission` block (edit/bash/webfetch →
 *     allow|ask|deny), with bash accepting a glob→decision map where the last
 *     matching rule wins (so the catch-all `*` is emitted first).
 *   - Instruction files: AGENTS.md is read automatically; extra always-on docs
 *     (here CHARTER.md) are listed in `instructions`.
 */

import type { ProvisionInput } from '../../types.js';
import type { ToolsFrontmatter } from '../../../types/tools.js';

/** Well-known base binary for the per-agent stdio MCP bundle the CLI ships. */
const MCP_BUNDLE_BASENAME = 'index.js';

// ENG-7931: opencode's headless `serve` runtime CANNOT load the models.dev
// provider package `@ai-sdk/xai` (it throws `UnsupportedApiError: … aisdk:@ai-sdk/xai`;
// `opencode run` can load it, `serve` cannot). xAI's HTTP API is OpenAI-compatible,
// so we route xai models through `@ai-sdk/openai-compatible` against xAI's endpoint
// instead. The provider id MUST NOT be `xai` - for a models.dev-known provider id
// opencode uses the registry's package (`@ai-sdk/xai`) and IGNORES a config `npm`
// override, re-triggering the failure. grok is not a models.dev provider id
// (that registry's xAI id is `xai`; `groq` is an unrelated provider), so opencode
// honours this custom provider block. Validated live: `serve` returns real grok-4.5
// inference with this shape, the built-in `xai` id does not.
const XAI_COMPAT_PROVIDER_ID = 'grok';
const XAI_COMPAT_BASE_URL = 'https://api.x.ai/v1';
const XAI_API_KEY_ENV = 'XAI_API_KEY';

/**
 * Translate the agent's stored model id into opencode's `provider/model` form.
 * opencode is multi-provider, so a bare `claude-*` / `gpt-*` / `gemini-*` id is
 * namespaced to its provider (anthropic / openai / google); an id that already
 * carries a `provider/` prefix (e.g. an OpenRouter-namespaced `openrouter/x-ai/grok-…`)
 * passes through untouched. xAI (`xai/grok-…` per ENG-7928, or a bare `grok-…`)
 * is remapped to the serve-usable openai-compatible custom provider (ENG-7931).
 */
export function toOpencodeModel(primaryModel?: string | null): string {
  const m = (primaryModel || 'claude-opus-4-7').trim();
  // xAI must route through the openai-compatible custom provider (ENG-7931),
  // NOT the built-in `xai` the serve can't load. Handle the resolved
  // `xai/grok-…` form (ENG-7928) and an already-compat `grok/…`.
  const xaiPrefixed = m.match(/^xai\/(.+)$/);
  if (xaiPrefixed) return `${XAI_COMPAT_PROVIDER_ID}/${xaiPrefixed[1]}`;
  if (m.includes('/')) return m;
  if (/^claude/i.test(m)) return `anthropic/${m}`;
  if (/^(gpt|o\d|chatgpt)/i.test(m)) return `openai/${m}`;
  if (/^gemini/i.test(m)) return `google/${m}`;
  if (/^grok/i.test(m)) return `${XAI_COMPAT_PROVIDER_ID}/${m}`;
  // Default to Anthropic — the fleet's baseline provider.
  return `anthropic/${m}`;
}

/** Provider id (left of the `/`) for the resolved opencode model. */
function providerOf(opencodeModel: string): string {
  return opencodeModel.split('/', 1)[0] ?? 'anthropic';
}

/**
 * Emit the `provider` block wiring the resolved provider's API key from the
 * runtime env via opencode's `{env:...}` substitution. Mirrors the .env
 * conventions `writeAuthProfiles` writes (`<PROVIDER>_API_KEY`).
 *
 * The xAI-compat provider (ENG-7931) needs the full openai-compatible wiring
 * (npm + baseURL + an explicit `models` map) because it is a custom id opencode
 * can't resolve from models.dev.
 */
function buildProvider(opencodeModel: string): Record<string, unknown> {
  const provider = providerOf(opencodeModel);
  if (provider === XAI_COMPAT_PROVIDER_ID) {
    // `grok/grok-4.5` → model id `grok-4.5` (the value sent to xAI's API).
    const modelId = opencodeModel.slice(provider.length + 1) || opencodeModel;
    return {
      [provider]: {
        npm: '@ai-sdk/openai-compatible',
        name: 'xAI',
        options: {
          baseURL: XAI_COMPAT_BASE_URL,
          apiKey: `{env:${XAI_API_KEY_ENV}}`,
        },
        models: { [modelId]: { name: modelId } },
      },
    };
  }
  const keyEnv: Record<string, string> = {
    anthropic: 'ANTHROPIC_API_KEY',
    openai: 'OPENAI_API_KEY',
    google: 'GOOGLE_GENERATIVE_AI_API_KEY',
    xai: 'XAI_API_KEY',
  };
  const envVar = keyEnv[provider] ?? `${provider.toUpperCase()}_API_KEY`;
  return { [provider]: { options: { apiKey: `{env:${envVar}}` } } };
}

/**
 * Split an opencode `provider/model` string into the `{providerID, id}` shape
 * opencode's session-create + prompt APIs require (ENG-7931). Returns null for a
 * bare/empty id. The serve does NOT apply the config's default model to
 * API-created sessions, so the manager must pass this explicitly.
 */
export function parseOpencodeModelRef(
  opencodeModel: string | null | undefined,
): { providerID: string; id: string } | null {
  if (!opencodeModel) return null;
  const slash = opencodeModel.indexOf('/');
  if (slash <= 0 || slash === opencodeModel.length - 1) return null;
  return { providerID: opencodeModel.slice(0, slash), id: opencodeModel.slice(slash + 1) };
}

/**
 * Build opencode's `permission` block from the TOOLS manifest.
 *
 * opencode's permission surface (edit/bash/webfetch) is coarser than Claude
 * Code's per-glob `Read()/Write()` deny-list — notably it has no first-class
 * Read-glob deny. We approximate the SECRETS_DENY_PERMISSIONS intent at the
 * bash layer (deny commands that would exfiltrate secret material) and record
 * the residual gap in the spike README. Network allowlists (`ToolNetwork`)
 * are a gateway/wrapper concern and are not expressible here.
 */
export function buildOpencodePermission(tools?: ToolsFrontmatter): Record<string, unknown> {
  // ENG-7938: opencode agents run HEADLESS (`opencode serve`), driven over HTTP
  // by the manager's inbound bridge - there is NO interactive approver. So a
  // permission value of `ask` prompts nobody; it DEADLOCKS the turn: the tool
  // call hangs in `state=running` forever, the assistant turn never completes,
  // the bridge's waitIdle times out (~2 min) and the agent goes silent / shows
  // offline in direct chat. (Root-caused live on the nora prod agent: a `bash`
  // call hung indefinitely under `ask` and completed in <11s under `allow`; a
  // bare no-tool turn worked, which is why bring-up validation missed it.) So we
  // map governance to allow/deny ONLY here, never `ask`.
  //
  // bash defaults to `allow` (gated only by the explicit secret-exfil denies
  // below): a shell-less autonomous agent can't function, this matches the broad
  // tool access Claude Code agents already run with, and the real cross-agent
  // boundary is ADR-0047 per-agent process/container isolation (opencode refuses
  // to spawn on a multi-tenant host without it) - not per-command prompts.
  // webfetch (network egress) still honours the agent's network policy, but as a
  // clean `deny`, not a hang.
  const denyNetwork = tools?.global_controls?.default_network_policy !== 'allow';

  // Catch-all first (last match wins in opencode), then explicit secret-file
  // denies. These are shell-glob patterns matched against the whole command.
  const bash: Record<string, string> = {
    '*': 'allow',
    'cat *.env*': 'deny',
    'cat *.pem': 'deny',
    'cat *.key': 'deny',
    'cat **/.ssh/**': 'deny',
    'cat **/.aws/**': 'deny',
    'cat **/credentials*': 'deny',
    env: 'deny',
    'printenv*': 'deny',
  };

  return {
    // Agents edit their own workspace freely; cross-dir isolation is enforced
    // by the manager spawning each agent in its own project dir.
    edit: 'allow',
    webfetch: denyNetwork ? 'deny' : 'allow',
    bash,
    // ENG-8058: opencode's `question` permission governs the agent asking the
    // user (its built-in question/ask tool). Left unset it falls through to a
    // blocking default and DEADLOCKS the same way `ask` does on a headless
    // serve - nobody answers, the turn hangs ~180s, produces no reply, and the
    // agent shows offline (root-caused live on nora). `deny` is a clean
    // rejection so the model proceeds instead of blocking. This is NOT solved
    // by skip-permissions/yolo, which bypasses tool APPROVAL, not the agent
    // ASKING (Claude Code agents can still wedge on AskUserQuestion with
    // --dangerously-skip-permissions on).
    question: 'deny',
  };
}

/**
 * The `augmented` control-plane MCP server, in opencode's local-server shape.
 * Points at the same `~/.augmented/_mcp/index.js` bundle the CLI deploys for
 * Claude Code, so both frameworks share one runtime. Env values that must
 * resolve per-spawn (`AGT_API_KEY`, `AGT_RUN_ID`, host/app URLs, PATH/HOME)
 * use opencode `{env:...}` substitution instead of Claude Code `${...}`.
 */
export function buildAugmentedMcpServer(
  input: ProvisionInput,
  mcpBundlePath: string,
): Record<string, unknown> {
  return {
    type: 'local',
    command: ['node', mcpBundlePath],
    environment: {
      AGT_HOST: '{env:AGT_HOST}',
      AGT_API_KEY: '{env:AGT_API_KEY}',
      AGT_AGENT_ID: input.agent.agent_id,
      AGT_AGENT_CODE_NAME: input.agent.code_name,
      AGT_RUN_ID: '{env:AGT_RUN_ID}',
      AGT_APP_URL: '{env:AGT_APP_URL}',
      PATH: '{env:PATH}',
      HOME: '{env:HOME}',
    },
    enabled: true,
  };
}

/**
 * Build the full `opencode.json` object for an agent. Pure — no filesystem
 * access — so it is trivially unit-testable (see opencode-adapter.test.ts).
 */
export function buildOpencodeConfig(
  input: ProvisionInput,
  opts: { mcpBundlePath: string },
): Record<string, unknown> {
  const { agent, toolsFrontmatter } = input;
  const model = toOpencodeModel(agent.primary_model);

  // NOTE: opencode validates opencode.json STRICTLY and rejects ANY unrecognized
  // top-level key — a single unknown key ("Unrecognized key: …") invalidates the
  // WHOLE config, so opencode silently falls back to its built-in defaults
  // (default provider + whatever key is in env), ignoring the model, provider,
  // permission and mcp we generate here. So we must emit ONLY schema-recognized
  // keys. In particular do NOT add a provision-metadata block (the claude-code
  // adapter's `_augmented` in settings.json has no opencode equivalent) - the
  // agent's runtime identity is already available via the augmented MCP server's
  // env (AGT_AGENT_ID / AGT_AGENT_CODE_NAME). See ENG-7882.
  return {
    $schema: 'https://opencode.ai/config.json',
    model,
    provider: buildProvider(model),
    // AGENTS.md is auto-loaded by opencode every session; CHARTER.md is the
    // machine-truth governance doc we want always in context alongside it.
    instructions: ['CHARTER.md'],
    permission: buildOpencodePermission(toolsFrontmatter),
    mcp: {
      augmented: buildAugmentedMcpServer(input, opts.mcpBundlePath),
    },
  };
}

/**
 * ENG-7959: merge a freshly generated `opencode.json` with what is already on
 * disk, preserving incrementally-added MCP servers.
 *
 * opencode's `mcp` block is CO-OWNED: `buildOpencodeConfig` (the generator) owns
 * the top-level config (model, provider, permission, instructions) and the
 * `augmented` MCP entry, while integration and channel MCP servers are appended
 * later by the adapter's `writeMcpServer` / `writeChannelCredentials` directly
 * into the SAME provision file (the one the serve reads and the runtime's
 * materializer lifts the `mcp` block from). A naive overwrite on re-provision
 * would wipe those extra servers every poll, so the manager re-adds them,
 * churning the serve.
 *
 * The merge keeps the generator's config and unions the two `mcp` maps
 * (generator wins for its own keys). The returned `generatorSlice` /
 * `existingSlice` are canonical JSON of ONLY the generator-owned portion (the
 * generator's top-level keys, and within `mcp` only the generator's own server
 * keys), serialized in the generator's key order so the caller can hash both
 * sides and skip the write when the generator-owned content is unchanged -
 * incremental MCP additions must NOT trip a rewrite. Mirrors the claude-code
 * adapter's `.mcp.json` merge, adapted to opencode's full-config file shape.
 *
 * Pure/exported for unit testing. Parse failures on either side degrade to `{}`,
 * matching the manager's tolerant inline behaviour for a corrupt on-disk file.
 */
export interface OpencodeConfigMerge {
  /** Merged config to write (pretty-printed). */
  content: string;
  /** Canonical generator-owned slice of the generator config (hash source). */
  generatorSlice: string;
  /** Canonical generator-owned slice of the on-disk config, or null if absent. */
  existingSlice: string | null;
}

export function mergeOpencodeConfigArtifact(
  generatorContent: string,
  existingContent: string | null,
): OpencodeConfigMerge {
  // A plain (non-null, non-array) object, else `{}`. Guards a malformed on-disk
  // `mcp` (scalar / array / null) so the `in` checks below never throw and a
  // bad `mcp:[...]` doesn't spread array indices into the merged config.
  const asRecord = (v: unknown): Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

  const parse = (raw: string): Record<string, unknown> => {
    try {
      return asRecord(JSON.parse(raw) as unknown);
    } catch {
      return {};
    }
  };
  const generatorCfg = parse(generatorContent);
  const existingCfg = existingContent ? parse(existingContent) : {};
  const generatorMcp = asRecord(generatorCfg['mcp']);
  const existingMcp = asRecord(existingCfg['mcp']);

  const merged = { ...generatorCfg, mcp: { ...existingMcp, ...generatorMcp } };

  // Generator-owned slice: the generator's top-level keys, plus any top-level key
  // present ONLY on disk (so a stale generator key the current generator no longer
  // emits surfaces on the existing side and forces a rewrite that drops it), and
  // within `mcp` only the generator's own server keys (extra integration/channel
  // servers stay invisible, so they never churn the serve). Iterate a stable key
  // order so both sides serialize identically when the generator-owned content
  // matches: generator keys first (generator order), then disk-only extras sorted.
  const sliceKeys = (cfg: Record<string, unknown>): string[] => [
    ...Object.keys(generatorCfg),
    ...Object.keys(cfg).filter((k) => !(k in generatorCfg)).sort(),
  ];
  const slice = (cfg: Record<string, unknown>): string => {
    const out: Record<string, unknown> = {};
    for (const k of sliceKeys(cfg)) {
      if (k === 'mcp') {
        const srcMcp = asRecord(cfg['mcp']);
        const mcpSlice: Record<string, unknown> = {};
        for (const s of Object.keys(generatorMcp)) if (s in srcMcp) mcpSlice[s] = srcMcp[s];
        out['mcp'] = mcpSlice;
      } else if (k in cfg) {
        out[k] = cfg[k];
      }
    }
    return JSON.stringify(out);
  };

  return {
    content: JSON.stringify(merged, null, 2),
    generatorSlice: slice(generatorCfg),
    existingSlice: existingContent ? slice(existingCfg) : null,
  };
}

export { MCP_BUNDLE_BASENAME };
